import { useMemo, useState } from 'react';
import { WatercolorBloom } from '../components/WatercolorBloom';

type TripProfile = 'points' | 'medical' | 'expensive' | 'adventure' | 'rental' | 'preexisting';
type Topic = {
  id: string;
  title: string;
  short: string;
  card: string;
  standalone: string;
  action: string;
  severity: 'green' | 'yellow' | 'red' | 'violet';
  tags: string[];
};

const topics: Topic[] = [
  {
    id: 'credit-card-stack',
    title: 'When credit-card coverage is enough',
    short: 'Strong for common-carrier disruption, bags, and covered cancellation — especially when a Sapphire card triggers on partial fare/taxes.',
    card: 'Use the card for award taxes/fees or some portion of the common-carrier fare. NerdWallet lists Sapphire Reserve/Preferred at up to $10k per person / $20k per trip for trip cancellation/interruption, baggage delay, lost luggage, trip delay, and primary rental CDW limits.',
    standalone: 'Do not duplicate expensive trip-cancellation coverage unless prepaid nonrefundable exposure exceeds card limits or you need broader cancellation reasons.',
    action: 'Charge at least the flight taxes/fees to the best travel-protection card, then save the benefits guide PDF before departure.',
    severity: 'green',
    tags: ['Sapphire', 'award taxes', 'covered reasons']
  },
  {
    id: 'medical-gap',
    title: 'The medical gap abroad',
    short: 'This is the big miss: most cards and many U.S. health plans do not behave like real international medical insurance.',
    card: 'Travel accident insurance is not medical care; it is generally death/dismemberment-style coverage. Frequent Miler notes Chase is unusual among ultra-premium cards for emergency medical/dental, but the amount is limited compared with a standalone medical policy.',
    standalone: 'PTA 132’s Lisa Jarvis recommends a cheap per-trip policy with $0 trip cost to keep medical/evacuation/delay/bag support while stripping out redundant cancellation coverage.',
    action: 'For international trips, buy medical + evacuation coverage even if the trip itself is mostly points.',
    severity: 'red',
    tags: ['medical', 'evacuation', '$0 trip cost']
  },
  {
    id: 'evacuation',
    title: 'Medical evacuation is the catastrophe line item',
    short: 'The State Department says air ambulance evacuation to the U.S. can run $20k–$200k and most health plans do not pay to bring you home.',
    card: 'Some premium cards offer emergency evacuation/transportation, but terms vary and caps can matter. Confirm who decides medical necessity and whether preauthorization is required.',
    standalone: 'Look for six-figure evacuation benefits plus a 24/7 assistance hotline that can direct care, translate, and coordinate with family.',
    action: 'Anchor the policy on evacuation first, then compare delay/bag extras second.',
    severity: 'red',
    tags: ['$20k–$200k', 'air ambulance', 'hotline']
  },
  {
    id: 'cancel-any-reason',
    title: 'Cancel-for-any-reason vs covered reasons',
    short: 'Cards and normal policies do not cover “I changed my mind.” CFAR is the paid escape hatch, usually partial reimbursement only.',
    card: 'Credit-card cancellation/interruption generally needs a covered reason: illness, death, severe weather, jury duty, etc.',
    standalone: 'NerdWallet says CFAR typically returns about 50%–75% of prepaid costs and often must be purchased within 10–21 days of initial payment.',
    action: 'Buy CFAR only for expensive, emotionally uncertain, or logistics-sensitive trips where partial recovery is worth the premium.',
    severity: 'yellow',
    tags: ['CFAR', '10–21 days', '50–75%']
  },
  {
    id: 'preexisting',
    title: 'Pre-existing conditions need early action',
    short: 'If you need a pre-existing condition waiver, timing and declared trip cost matter.',
    card: 'Card benefits are not a reliable way to solve pre-existing medical condition complexity.',
    standalone: 'NerdWallet notes waivers usually require buying within the provider’s specified window, often 10–21 days, and insuring all prepaid nonrefundable trip costs.',
    action: 'If health history is a factor, quote insurance immediately after the first trip deposit instead of waiting until final payment.',
    severity: 'violet',
    tags: ['waiver', 'early purchase', 'full trip cost']
  },
  {
    id: 'bags',
    title: 'Delayed/lost bags: stack policy + passenger rights',
    short: 'Cards and standalone policies can reimburse essentials, but airlines also owe reasonable expenses under baggage passenger-right rules.',
    card: 'NerdWallet lists Sapphire baggage delay up to $100/day for five days after six hours, and lost luggage up to $3,000/passenger, subject to terms.',
    standalone: 'Standalone policies may add baggage delay/loss support and assistance services that chase the airline while you keep traveling.',
    action: 'File the airline baggage claim before leaving baggage claim, ask for the reasonable-expenses claim form, and keep every receipt.',
    severity: 'yellow',
    tags: ['PIR/claim', 'receipts', 'reasonable expenses']
  },
  {
    id: 'rental-car',
    title: 'Rental cars: primary CDW is useful, not liability',
    short: 'Premium cards can be excellent for collision/theft of the rental car, but they usually do not replace liability coverage.',
    card: 'Frequent Miler highlights Chase and Citi as standouts for worldwide rental-car coverage; NerdWallet lists Sapphire Preferred/Reserve as primary CDW with caps, subject to exclusions.',
    standalone: 'A travel policy or rental counter coverage may still be needed for liability, excluded vehicle types, long rentals, or countries/roads excluded by the card.',
    action: 'Pay the full rental with the card, decline only the coverage your card actually replaces, and verify liability separately.',
    severity: 'violet',
    tags: ['primary CDW', 'liability', 'exclusions']
  },
  {
    id: 'adventure',
    title: 'Adventure travel changes the policy',
    short: 'High altitude hiking, diving, paragliding, ziplining, and other “extreme” activities may be excluded without an add-on.',
    card: 'Do not expect a credit-card benefit to rescue an excluded activity injury.',
    standalone: 'PTA 132 flags altitude above roughly 9,000 feet as a common surprise extreme-sport trigger. Buy the add-on if the itinerary even might include it.',
    action: 'Scan the exclusions list against the actual itinerary — altitude, scooters, boats, water sports, wildlife, and adventure operators.',
    severity: 'red',
    tags: ['9,000 ft', 'exclusions', 'add-on']
  }
];

const profiles: Record<TripProfile, { title: string; subtitle: string; recommendation: string; focus: string[] }> = {
  points: {
    title: 'Points trip / low cash exposure',
    subtitle: 'Award flights, hotel points, taxes/fees on a premium travel card.',
    recommendation: 'Best stack: card protections for cancellation/interruption + standalone $0-trip-cost medical/evacuation policy.',
    focus: ['credit-card-stack', 'medical-gap', 'evacuation', 'bags']
  },
  medical: {
    title: 'International family trip',
    subtitle: 'Kids, older travelers, Medicare, remote destinations, or limited local hospitals.',
    recommendation: 'Do not rely on cards. Buy per-trip medical/evacuation with a real assistance hotline and carry a high-limit credit card for upfront payment.',
    focus: ['medical-gap', 'evacuation', 'preexisting', 'bags']
  },
  expensive: {
    title: 'Expensive prepaid trip',
    subtitle: 'Cruise, safari, tour, nonrefundable lodging, or large deposits.',
    recommendation: 'Compare card limits against prepaid exposure. If you exceed limits or need broader reasons, insure the real trip cost and consider CFAR early.',
    focus: ['credit-card-stack', 'cancel-any-reason', 'preexisting', 'medical-gap']
  },
  adventure: {
    title: 'Adventure / altitude trip',
    subtitle: 'Peru, Nepal, Alps, diving, scooters, paragliding, ziplining, trekking.',
    recommendation: 'Buy standalone coverage with the correct sports/adventure rider; the cheap base policy is only cheap if the activity is covered.',
    focus: ['adventure', 'medical-gap', 'evacuation', 'preexisting']
  },
  rental: {
    title: 'Rental-car heavy trip',
    subtitle: 'Road trip, international rental, islands, rural routes, premium vehicles.',
    recommendation: 'Use a card with primary CDW, but separately confirm liability, country exclusions, vehicle exclusions, and roadside rules.',
    focus: ['rental-car', 'credit-card-stack', 'medical-gap', 'bags']
  },
  preexisting: {
    title: 'Known health condition',
    subtitle: 'Recent treatment, medication changes, older travelers, or family-health risk.',
    recommendation: 'Quote immediately after first deposit. Waivers are timing-sensitive and may require insuring all prepaid nonrefundable costs.',
    focus: ['preexisting', 'medical-gap', 'evacuation', 'cancel-any-reason']
  }
};

const emergencySteps = [
  { label: '01', title: 'Open the policy first', body: 'Know the delay trigger, per-person cap, hotline number, and preauthorization rules before standing in a line.' },
  { label: '02', title: 'Screenshot everything', body: 'Airline app alerts vanish after arrival. Capture delay reasons, cancellation messages, bag tags, boarding passes, and hotel/meal receipts.' },
  { label: '03', title: 'Call the right operator', body: 'For medical: insurance assistance hotline. For rebooking: airline phone/chat. For bags: baggage office before leaving the airport.' },
  { label: '04', title: 'Avoid bad vouchers', body: 'For EU/UK passenger-right claims, do not accept a travel voucher as compensation if cash compensation may apply.' },
  { label: '05', title: 'Spend within the cap', body: 'If the policy gives $150–$200/person/day or card gives $500/person, book safe lodging, meals, transport, and essentials confidently.' },
  { label: '06', title: 'File both lanes', body: 'Insurance/card claims reimburse costs. Passenger-right claims like EU261 can be separate cash compensation when airline-caused delays qualify.' }
];

const sourceLinks = [
  { name: 'PTA 132 / Points Talk with Lisa Jarvis', url: 'https://podcasts.apple.com/us/podcast/points-talk-your-travel-dreams-made-possible-by-points/id1660701356?i=1000770732439', note: '$0 trip-cost method, Sapphire partial-charge strategy, hotline/documentation/passenger-right tactics.' },
  { name: 'NerdWallet: What Does Travel Insurance Cover?', url: 'https://www.nerdwallet.com/travel/learn/what-does-travel-insurance-cover', note: 'Coverage categories, CFAR timing/reimbursement range, pre-existing waiver timing, rental CDW notes.' },
  { name: 'NerdWallet: Credit Cards That Provide Travel Insurance', url: 'https://www.nerdwallet.com/travel/learn/credit-cards-that-provide-travel-insurance', note: 'Credit-card benefit examples for trip cancellation/interruption, delay, baggage, rental car.' },
  { name: 'Frequent Miler: Ultra-Premium Credit Card Travel Protections', url: 'https://frequentmiler.com/ultra-premium-credit-card-travel-insurance/', note: 'Card-by-card comparison; Chase leads automatic protections; evacuation/medical/rental-car caveats.' },
  { name: 'Frequent Miler: Award Flight Coverage', url: 'https://frequentmiler.com/credit-card-travel-insurance-cover-award-flights/', note: 'Taxes/fees on award tickets can count where partial payment is enough; Chase/Citi/Amex differences.' },
  { name: 'U.S. State Department: Medicine and Health Abroad', url: 'https://travel.state.gov/en/international-travel/planning/guidance/medicine-health.html', note: 'U.S. government does not pay medical bills abroad; evacuation can cost $20k–$200k.' }
];

const profileOptions = Object.keys(profiles) as TripProfile[];

export default function Insurance() {
  const [profile, setProfile] = useState<TripProfile>('points');
  const selected = profiles[profile];
  const highlighted = useMemo(() => selected.focus.map(id => topics.find(t => t.id === id)).filter(Boolean) as Topic[], [selected]);

  return (
    <div className="insurance-page">
      <style>{styles}</style>
      <section className="card card-dark brackets insurance-hero">
        <WatercolorBloom variant="duo" size={620} opacity={0.42} style={{ position: 'absolute', top: -210, right: -140 }} />
        <WatercolorBloom variant="uv" size={420} opacity={0.3} style={{ position: 'absolute', bottom: -190, left: -130 }} />
        <div className="insurance-hero-copy">
          <span className="label" style={{ color: 'var(--c-cyan)' }}>// PTA 132 travel protection stack</span>
          <h1 className="h1" style={{ color: 'white', marginTop: 8 }}>Insurance<br/><span className="gradient-text">Flight Deck</span></h1>
          <p className="body">A practical, visual-first decision board for points travelers: when the card is enough, when to buy standalone coverage, and what to do in the first 30 minutes when travel goes sideways.</p>
        </div>
        <div className="insurance-hero-stats">
          <Metric value="$0" label="trip-cost method" />
          <Metric value="$250k" label="medical target from PTA" />
          <Metric value="$500k" label="evac target from PTA" />
          <Metric value="24/7" label="assistance hotline" />
        </div>
      </section>

      <section className="insurance-controls card">
        <div>
          <span className="label-bright">// choose trip profile</span>
          <h2 className="h3" style={{ marginTop: 8 }}>Decision engine</h2>
          <p className="body muted">This is not legal or insurance advice; it is a planning checklist. Always read the actual benefits guide and policy wording.</p>
        </div>
        <div className="insurance-profile-grid" role="group" aria-label="Trip profiles">
          {profileOptions.map(key => <button key={key} type="button" className={`insurance-profile ${profile === key ? 'active' : ''}`} onClick={() => setProfile(key)} aria-pressed={profile === key}>{profiles[key].title}</button>)}
        </div>
      </section>

      <section className="insurance-decision-grid">
        <div className="card card-dark insurance-radar">
          <div className="insurance-radar-head">
            <div><span className="label" style={{ color: 'var(--c-cyan)' }}>// selected route</span><h2 className="h3">{selected.title}</h2></div>
            <span className="pill pill-booked">recommended stack</span>
          </div>
          <InsuranceRadar activeIds={selected.focus} />
        </div>
        <aside className="card insurance-reco brackets">
          <span className="label-bright">// recommendation</span>
          <h2 className="h2">{selected.title}</h2>
          <p className="body muted">{selected.subtitle}</p>
          <p className="insurance-verdict">{selected.recommendation}</p>
          <div className="insurance-stack">
            <StackLayer title="Card lane" body="Trip cancellation/interruption, trip delay, baggage delay/loss, and rental CDW when terms are met." />
            <StackLayer title="Policy lane" body="Medical, evacuation, 24/7 assistance, adventure/pre-existing/CFAR riders when the trip needs them." />
            <StackLayer title="Rights lane" body="EU/UK261 cash, baggage reasonable expenses, and airline duty-of-care separate from insurance." />
          </div>
        </aside>
      </section>

      <section>
        <div className="row-between" style={{ marginBottom: 14, flexWrap: 'wrap' }}>
          <h2 className="h3">// Priority cards for this profile</h2>
          <span className="label">Tap another profile above to reshuffle</span>
        </div>
        <div className="insurance-topic-grid">
          {highlighted.map(topic => <TopicCard key={topic.id} topic={topic} featured />)}
        </div>
      </section>

      <section>
        <div className="row-between" style={{ marginBottom: 14, flexWrap: 'wrap' }}>
          <h2 className="h3">// Full coverage matrix</h2>
          <span className="label">Card vs standalone vs action</span>
        </div>
        <div className="insurance-matrix">
          {topics.map(topic => <TopicCard key={topic.id} topic={topic} />)}
        </div>
      </section>

      <section className="card card-dark insurance-timeline brackets">
        <WatercolorBloom variant="cyan" size={440} opacity={0.25} style={{ position: 'absolute', right: -150, top: -160 }} />
        <div className="insurance-timeline-copy">
          <span className="label" style={{ color: 'var(--c-cyan)' }}>// when travel goes wrong</span>
          <h2 className="h2">First 30 minutes</h2>
          <p className="body">Lisa Jarvis’s core move: know what you can spend, skip the wrong lines, document in real time, then file the right claim lane later.</p>
        </div>
        <div className="insurance-steps">
          {emergencySteps.map(step => <article key={step.label} className="insurance-step"><span>{step.label}</span><h3>{step.title}</h3><p>{step.body}</p></article>)}
        </div>
      </section>

      <section className="insurance-checklist-grid">
        <Checklist title="Before departure" items={['Save card benefits guide and policy PDF offline.', 'Put award taxes/fees or trip charge on the card with the right protections.', 'Buy standalone medical/evacuation for international trips; add CFAR/pre-existing/adventure riders early if needed.', 'Carry policy number, hotline, prescriptions by generic name, and a credit card with room for upfront medical costs.']} />
        <Checklist title="Claim packet" items={['Boarding passes, itinerary, receipts, and proof of payment card.', 'Screenshots of delay/cancellation reason and final arrival time.', 'Medical bills, diagnosis notes, discharge paperwork, and proof of payment.', 'Bag report/PIR, baggage tag, delivery record, essential-purchase receipts.']} />
        <Checklist title="Passenger-right triggers" items={['EU/UK/Switzerland/Norway/Iceland arrival delay of 3+ hours caused by airline-controlled reasons.', 'Do not take a voucher if cash compensation is possible.', 'Delayed bag: ask for reasonable-expenses reimbursement by name.', 'Weather is usually not compensable, but still screenshot the airline’s stated reason.']} />
      </section>

      <section className="card insurance-sources">
        <span className="label-bright">// sources and caveats</span>
        <div className="insurance-source-grid">
          {sourceLinks.map(source => <a key={source.url} href={source.url} target="_blank" rel="noreferrer" className="insurance-source"><strong>{source.name}</strong><span>{source.note}</span></a>)}
        </div>
        <p className="body muted" style={{ marginTop: 16 }}>Amounts and card benefits change. This page intentionally frames benefits as decision guidance, not as a substitute for the claim administrator’s current terms.</p>
      </section>
    </div>
  );
}

function Metric({ value, label }: { value: string; label: string }) {
  return <div className="insurance-metric"><div className="mono-num">{value}</div><div className="label">{label}</div></div>;
}

function StackLayer({ title, body }: { title: string; body: string }) {
  return <article><h3>{title}</h3><p>{body}</p></article>;
}

function TopicCard({ topic, featured = false }: { topic: Topic; featured?: boolean }) {
  return <article className={`card insurance-topic ${featured ? 'featured' : ''} tone-${topic.severity}`}>
    <div className="insurance-topic-top"><span className="label-bright">{topic.id.replaceAll('-', ' ')}</span><span className={`insurance-dot ${topic.severity}`} /></div>
    <h3 className="h3">{topic.title}</h3>
    <p className="body">{topic.short}</p>
    <div className="insurance-lanes">
      <Lane title="Card" body={topic.card} />
      <Lane title="Standalone" body={topic.standalone} />
      <Lane title="Action" body={topic.action} />
    </div>
    <div className="insurance-chip-row">{topic.tags.map(tag => <span key={tag} className="pill pill-completed">{tag}</span>)}</div>
  </article>;
}

function Lane({ title, body }: { title: string; body: string }) {
  return <dl><dt>{title}</dt><dd>{body}</dd></dl>;
}

function Checklist({ title, items }: { title: string; items: string[] }) {
  return <section className="card insurance-checklist"><span className="label-bright">// checklist</span><h2 className="h3">{title}</h2><ul>{items.map(item => <li key={item}>{item}</li>)}</ul></section>;
}

function InsuranceRadar({ activeIds }: { activeIds: string[] }) {
  const nodes = topics.map((topic, index) => {
    const angle = (Math.PI * 2 * index) / topics.length - Math.PI / 2;
    const active = activeIds.includes(topic.id);
    const radius = active ? 188 : 148;
    return { topic, active, x: 250 + Math.cos(angle) * radius, y: 250 + Math.sin(angle) * radius };
  });
  const activeNodes = nodes.filter(n => n.active);
  const polygon = activeNodes.map(n => `${n.x.toFixed(1)},${n.y.toFixed(1)}`).join(' ');
  return <svg className="insurance-radar-svg" viewBox="0 0 500 500" role="img" aria-label="Insurance decision radar">
    <defs>
      <radialGradient id="insBg" cx="50%" cy="45%" r="70%"><stop offset="0" stopColor="#26306a"/><stop offset="1" stopColor="#080b1e"/></radialGradient>
      <linearGradient id="insPoly" x1="0%" x2="100%"><stop stopColor="#00F0FF"/><stop offset=".55" stopColor="#FF2BD6"/><stop offset="1" stopColor="#A7FF2A"/></linearGradient>
      <filter id="insGlow"><feGaussianBlur stdDeviation="3" result="blur"/><feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
    </defs>
    <rect width="500" height="500" rx="28" fill="url(#insBg)" />
    {[80,140,200].map(r => <circle key={r} cx="250" cy="250" r={r} fill="none" stroke="rgba(232,217,255,.16)" />)}
    {nodes.map(n => <line key={`${n.topic.id}-line`} x1="250" y1="250" x2={n.x} y2={n.y} stroke="rgba(232,217,255,.12)" />)}
    {polygon && <polygon points={polygon} fill="rgba(0,240,255,.15)" stroke="url(#insPoly)" strokeWidth="3" filter="url(#insGlow)" />}
    {nodes.map(n => <g key={n.topic.id} transform={`translate(${n.x} ${n.y})`}>
      <circle r={n.active ? 8 : 5} fill={n.active ? '#A7FF2A' : '#E8D9FF'} stroke={n.active ? '#00F0FF' : 'rgba(255,255,255,.45)'} strokeWidth="2" />
      <text x="12" y="4" fill="white" fontSize="11" fontFamily="var(--font-ui)" fontWeight="800" letterSpacing=".06em">{n.topic.title.split(':')[0].slice(0, 22)}</text>
    </g>)}
    <text x="250" y="246" textAnchor="middle" fill="#fff" fontFamily="var(--font-display)" fontSize="24" fontWeight="900">STACK</text>
    <text x="250" y="270" textAnchor="middle" fill="#E8D9FF" fontFamily="var(--font-ui)" fontSize="12">card + policy + rights</text>
  </svg>;
}

const styles = `
.insurance-page { display: flex; flex-direction: column; gap: 24px; }
.insurance-hero { display: grid; grid-template-columns: minmax(0, 1.35fr) minmax(270px, .85fr); gap: 28px; align-items: end; min-height: 300px; padding: 34px; overflow: hidden; }
.insurance-hero-copy, .insurance-hero-stats { position: relative; z-index: 1; }
.insurance-hero-copy p { color: var(--c-lavender); max-width: 760px; margin: 16px 0 0; }
.insurance-hero-stats { display: grid; grid-template-columns: repeat(2, minmax(120px, 1fr)); gap: 12px; }
.insurance-metric { border: 1px solid rgba(232,217,255,.22); background: rgba(255,255,255,.06); border-radius: var(--r-md); padding: 14px; }
.insurance-metric .mono-num { font-size: 30px; color: white; line-height: 1; }
.insurance-metric .label { color: rgba(232,217,255,.76); margin-top: 6px; }
.insurance-controls { display: grid; grid-template-columns: minmax(250px, .55fr) minmax(0, 1fr); gap: 18px; align-items: center; }
.insurance-profile-grid { position: relative; z-index: 1; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
.insurance-profile { border: 1px solid rgba(123,92,255,.22); border-radius: 999px; background: rgba(255,255,255,.72); color: var(--t-muted); padding: 10px 12px; font-family: var(--font-ui); font-weight: 800; text-transform: uppercase; letter-spacing: .04em; }
.insurance-profile.active { background: var(--c-indigo); color: white; box-shadow: inset 0 -2px 0 var(--c-magenta), 0 10px 24px rgba(123,92,255,.18); }
.insurance-decision-grid { display: grid; grid-template-columns: minmax(0, 1.25fr) minmax(340px, .75fr); gap: 24px; }
.insurance-radar { padding: 20px; }
.insurance-radar-head { position: relative; z-index: 1; display: flex; justify-content: space-between; gap: 16px; align-items: flex-start; margin-bottom: 12px; color: white; }
.insurance-radar-svg { width: 100%; min-height: 470px; border-radius: 22px; }
.insurance-reco { padding: 24px; }
.insurance-reco .h2 { margin-top: 8px; }
.insurance-verdict { position: relative; z-index: 1; margin: 18px 0; padding: 16px; border-radius: 16px; border: 1px solid rgba(255,43,214,.25); background: linear-gradient(135deg, rgba(255,43,214,.10), rgba(0,240,255,.08)); font-weight: 800; line-height: 1.5; }
.insurance-stack { position: relative; z-index: 1; display: grid; gap: 12px; }
.insurance-stack article { border: 1px solid rgba(123,92,255,.18); border-radius: 14px; padding: 14px; background: rgba(255,255,255,.62); }
.insurance-stack h3 { margin: 0 0 5px; font-family: var(--font-display); font-size: 15px; text-transform: uppercase; }
.insurance-stack p { margin: 0; color: var(--t-muted); font-size: 13px; line-height: 1.45; }
.insurance-topic-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.insurance-matrix { display: grid; grid-template-columns: repeat(auto-fit, minmax(285px, 1fr)); gap: 18px; }
.insurance-topic { padding: 20px; border-color: rgba(123,92,255,.2); }
.insurance-topic.featured { border-color: var(--c-magenta); box-shadow: 0 12px 34px rgba(255,43,214,.16); }
.insurance-topic-top { position: relative; z-index: 1; display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.insurance-dot { width: 12px; height: 12px; border-radius: 50%; box-shadow: 0 0 14px currentColor; }
.insurance-dot.green { background: var(--c-lime); color: var(--c-lime); }
.insurance-dot.yellow { background: var(--c-yellow); color: var(--c-yellow); }
.insurance-dot.red { background: var(--c-magenta); color: var(--c-magenta); }
.insurance-dot.violet { background: var(--c-uv); color: var(--c-uv); }
.insurance-topic .h3 { margin: 10px 0; }
.insurance-lanes { position: relative; z-index: 1; display: grid; gap: 10px; margin-top: 14px; }
.insurance-lanes dl { border: 1px solid rgba(123,92,255,.16); border-radius: 12px; padding: 11px; margin: 0; background: rgba(255,255,255,.6); }
.insurance-lanes dt { font-family: var(--font-ui); font-weight: 800; text-transform: uppercase; letter-spacing: .07em; color: var(--c-uv); font-size: 12px; }
.insurance-lanes dd { margin: 4px 0 0; color: var(--t-muted); font-size: 12.5px; line-height: 1.45; }
.insurance-chip-row { position: relative; z-index: 1; display: flex; flex-wrap: wrap; gap: 7px; margin-top: 14px; }
.insurance-timeline { padding: 28px; overflow: hidden; display: grid; grid-template-columns: minmax(260px, .55fr) minmax(0, 1fr); gap: 22px; }
.insurance-timeline-copy { position: relative; z-index: 1; }
.insurance-timeline-copy p { color: var(--c-lavender); }
.insurance-steps { position: relative; z-index: 1; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.insurance-step { border: 1px solid rgba(232,217,255,.22); background: rgba(255,255,255,.06); border-radius: 16px; padding: 14px; }
.insurance-step span { font-family: var(--font-display); font-size: 24px; font-weight: 900; color: var(--c-cyan); }
.insurance-step h3 { margin: 8px 0 6px; font-family: var(--font-display); font-size: 15px; text-transform: uppercase; color: white; }
.insurance-step p { margin: 0; color: rgba(232,217,255,.78); font-size: 13px; line-height: 1.45; }
.insurance-checklist-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 18px; }
.insurance-checklist ul { position: relative; z-index: 1; margin: 14px 0 0; padding-left: 18px; color: var(--t-muted); line-height: 1.5; }
.insurance-checklist li { margin-bottom: 9px; }
.insurance-sources { padding: 22px; }
.insurance-source-grid { position: relative; z-index: 1; display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 12px; margin-top: 14px; }
.insurance-source { display: block; border: 1px solid rgba(123,92,255,.18); border-radius: 14px; padding: 14px; background: rgba(255,255,255,.62); color: var(--t-primary); }
.insurance-source strong { display: block; font-family: var(--font-ui); text-transform: uppercase; letter-spacing: .04em; }
.insurance-source span { display: block; margin-top: 6px; color: var(--t-muted); font-size: 13px; line-height: 1.45; }
@media (max-width: 1080px) { .insurance-topic-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .insurance-steps { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 960px) { .insurance-hero, .insurance-controls, .insurance-decision-grid, .insurance-timeline, .insurance-checklist-grid { grid-template-columns: 1fr; } .insurance-radar-svg { min-height: 380px; } }
@media (max-width: 640px) { .insurance-hero { padding: 24px; } .insurance-hero-stats, .insurance-profile-grid, .insurance-topic-grid, .insurance-steps { grid-template-columns: 1fr; } }
`;
